/**
 * ============================================================
 *  GOOP ZONE — lootOrbs.ts
 *  World Loot Entities: XP Orbs, Gold Coins, Health Pickups
 * ============================================================
 *
 *  When enemies die, they drop loot orbs into the world.
 *  Orbs float, pulse, and vacuum toward the player when close.
 *  Gold auto-collects at a wider radius.
 *  XP orbs require closer proximity (or a magnet upgrade).
 *
 *  Single XP orb type — value scales from percs/items later.
 *
 *  Drop into: client/src/game/lootOrbs.ts
 * ============================================================
 */

// ─── Types ───────────────────────────────────────────────────

export type OrbType = 'xp' | 'gold' | 'health';

export interface LootOrb {
  id: number;
  type: OrbType;
  value: number;          // XP amount, gold amount, or HP restored
  mergeCount?: number;    // BETA 21.34 — how many orbs bundled into this one (drives the ×N badge + tier ring)

  // World position
  x: number;
  y: number;

  // Physics
  vx: number;             // velocity from initial scatter
  vy: number;
  friction: number;       // deceleration rate

  // Vacuum state
  vacuuming: boolean;     // currently being pulled toward player
  vacuumSpeed: number;    // current vacuum pull speed (accelerates)

  // Visual
  pulsePhase: number;     // for glow animation
  scale: number;          // current render scale (grows on spawn)
  alpha: number;          // fade in on spawn
  age: number;            // seconds since spawn

  // Lifetime
  alive: boolean;
  despawnTimer: number;

  // BETA 23.6x — CO-OP PHASE 2 part D: OWNED ORBS rail 1 (multiplayer-plan
  // locked design — per-player owned orbs). The orb remembers which roster
  // player earned it (threaded from the kill's killerPlayerId stamp, part C).
  // undefined = legacy shared orb (unstamped source: barrels, weapon path,
  // devour drops). Identity only — no color / bonus / absorb-rule change yet.
  ownerPlayerId?: string;
}

export interface LootOrbState {
  orbs: LootOrb[];
  nextId: number;
  magnetTimer: number;   // Seconds remaining on active Chick Magnet — all orbs pulled continuously
}

// ─── Constants ───────────────────────────────────────────────

// BETA 16.37 — Cap raised + overflow path is now O(1).
// PSBOT cycle-30i §1: at Dylan's 9855-kill scale (~19,710 orbs spawned)
// the old MAX_ORBS=800 dropped ~95% of loot silently via O(n) array shift.
// Raise to 20K so a 10K-kill run never silently drops anything; if cap
// IS ever reached (extreme edge), overwrite oldest in place instead of
// shifting (O(1) vs O(n)). No auto-vacuum — Dylan: "i can add special
// items later that auto suck every so often. i have not added specials
// yet. so no on that for now."
const MAX_ORBS = 20000;
let _orbWriteCursor = 0; // ring-buffer cursor used when length === MAX_ORBS
// Orbs never expire — they only disappear when collected

// Distance culling — set very large so orbs anywhere on the map are never silently deleted.
// The Chick Magnet must be able to vacuum 100% of loot regardless of where it spawned.
const ORB_FREEZE_DIST2 = 1600 * 1600; // Beyond ~1600 units: freeze physics to save CPU
const ORB_CULL_DIST2   = 9999 * 9999; // Effectively never cull — orbs persist until collected
const ORB_SCATTER_SPEED = 220;       // Initial scatter velocity (increased for satisfying burst)
const ORB_FRICTION = 4.5;            // How fast scatter velocity decays

// Vacuum radii (world units) — kept close to each other so XP and gold feel the same
const XP_VACUUM_RADIUS = 140;       // Walk-over radius for XP
const GOLD_VACUUM_RADIUS = 160;     // Gold auto-collects at slightly wider radius
const HEALTH_VACUUM_RADIUS = 80;    // Health needs closer proximity

const VACUUM_ACCEL = 1400;          // How fast vacuum speed ramps up (snappier pull)
const VACUUM_MAX_SPEED = 650;       // Max vacuum pull speed (normal pull)
const MAGNET_VACUUM_SPEED = 1300;   // Magnet pulls 2× faster so orbs don't get left behind
// BETA 16.24 — PSBOT cycle-25 P0.6: ramp parameters for the chick magnet
// vacuum. Speed starts at 15% of MAGNET_VACUUM_SPEED and ramps to 100%
// over MAGNET_RAMP_SEC seconds with ease-in-quad. Tuned to outlast the
// 1.656-second chick magnet stinger.
const MAGNET_DURATION_SEC = 10.0;
const MAGNET_RAMP_SEC = 1.8;
const COLLECT_RADIUS = 24;          // Distance at which orb is "collected"

// BETA 17.82 — Orb merging. Dylan: "if so much gold or xp is on the
// ground then they should be combining. so that the map isnt showing
// litterally 100,000 xp orbs on the ground. it will be way less but when
// we gather it it will be the correct ammount of xp." Merge same-type
// orbs that have been sitting on the ground (age >= MERGE_MIN_AGE) into
// one orb with summed value when same-type alive count exceeds the
// trigger. Fresh orbs (mid-scatter) are untouched so the satisfying burst
// at spawn is preserved. Vacuuming orbs (en route to player) never merge
// — they're already being collected, value would double-count.
// BETA 21.34 — Dylan: keep orbs fun (lots of them) but BUNDLE nearby ones sooner so
// the screen isn't a shit-show, and show a ×N badge so you can see the juicy piles.
// Trigger 100 → 60, min-age 3.0 → 2.0, radius 28 → 32 (bundle a bit sooner + wider,
// still leaves plenty on the ground).
// BETA 21.36 — Dylan: "orbs are accumulating still, not all combining. we could be
// doing 50x. the more there are near each other (more than ~10 each) they need to
// combine, up to 99x." Bundle MUCH sooner + wider so nearby piles fuse into big ×N
// orbs instead of a sea of singles. Trigger 60→18, min-age 2.0→1.0, radius 32→48.
// BETA 21.50 — Dylan: "1000 orbs on the map, I only see ×3/×4, and they cause FPS
// drops." The merge was too timid (radius 48, every 0.5s, after 1s of age, only
// once 18 piled up) so piles stayed as hundreds of separate orbs = render cost.
// Much more aggressive now: wider radius + sooner + more often → 1000 orbs collapse
// into a few big ×N stacks fast, so far fewer entities to render.
const MERGE_TRIGGER_COUNT = 8;     // start merging once a small pile forms (was 18)
const MERGE_MIN_AGE = 0.35;         // merge almost immediately (was 1.0)
const MERGE_RADIUS = 120;           // 2.5× wider merge reach (was 48)
const MERGE_RADIUS2 = MERGE_RADIUS * MERGE_RADIUS;
const MERGE_INTERVAL_SEC = 0.2;     // merge pass ~5×/sec (was 0.5 = 2×/sec)
let _mergeAccum = 0;                // Accumulator since last merge pass

// Visual sizes (pixels at 1x zoom) — made bigger for visibility
const ORB_SIZES: Record<OrbType, number> = {
  xp:     9,
  gold:   8,
  health: 9,
};

// Colors
const ORB_COLORS: Record<OrbType, { fill: string; glow: string; inner: string }> = {
  xp:     { fill: '#44ccff', glow: '#2299dd', inner: 'rgba(255,255,255,0.4)' },
  gold:   { fill: '#ffd700', glow: '#cc9900', inner: 'rgba(255,255,255,0.3)' },
  health: { fill: '#ff5577', glow: '#dd2244', inner: 'rgba(255,255,255,0.35)' },
};

// ─── State Management ────────────────────────────────────────

export function createLootOrbState(): LootOrbState {
  // BETA 16.37 — reset the ring-buffer cursor when a new orb state spawns
  // so each run starts with a deterministic write position.
  _orbWriteCursor = 0;
  // BETA 17.82 — reset merge accumulator so the first merge pass of a new
  // run fires on its normal cadence (not immediately on frame 1).
  _mergeAccum = 0;
  return {
    orbs: [],
    nextId: 1,
    magnetTimer: 0,
  };
}

// BETA 21.52 — Dylan: "no leftover gold/XP on the ground when I start a new floor."
// Bulletproof clear for floor advance: empty the array AND reset the module-level
// ring-buffer cursor + merge accumulator (the old inline `orbs.length = 0` left
// those stale). Use this instead of touching state.orbs directly.
export function clearLootOrbs(state: LootOrbState): void {
  state.orbs.length = 0;
  _orbWriteCursor = 0;
  _mergeAccum = 0;
}

// BETA 17.82 — Merge nearby same-type orbs into one with summed value.
// Same-type, age >= MERGE_MIN_AGE, not currently vacuuming. Uses a spatial
// grid keyed by (type, cellX, cellY) for O(n) bucketing. Per-bucket merge
// is O(k²) but k is tiny (orbs per 28px cell rarely > 4). Merged orb sits
// at the value-weighted average position of its inputs. Value-based size
// scaling in renderLootOrbs makes the merged orb visually bigger so the
// player can see "that one's worth more."
// ⚠️ BETA 23.6x — CO-OP PHASE 2 part D KNOWN GAP (audit-confirmed, deferred BY DESIGN):
// this merge pass fuses orbs ACROSS ownership boundaries — the absorbed orb's
// value inherits the KEEPER's ownerPlayerId, which in co-op would launder or
// strand owned-orb value (SP: all owners compatible, byte-identical, zero harm).
// The merge rule MUST be decided with the orb-color/absorb part (options: key
// merges by owner, split merged value per owner, or owner-compatible-only) —
// it interacts with the OPEN most-dmg/final-blow bonus design. FIRST task of
// that part. Do NOT ship co-op absorb rules while this pass is owner-blind.
function mergeNearbyOrbs(orbs: LootOrb[]): void {
  // Per-type alive count gate — only merge types that have piled up
  let xpAlive = 0, goldAlive = 0, healthAlive = 0;
  for (const o of orbs) {
    if (!o.alive || o.vacuuming) continue;
    if (o.type === 'xp') xpAlive++;
    else if (o.type === 'gold') goldAlive++;
    else healthAlive++;
  }
  const shouldMergeXp = xpAlive >= MERGE_TRIGGER_COUNT;
  const shouldMergeGold = goldAlive >= MERGE_TRIGGER_COUNT;
  const shouldMergeHealth = healthAlive >= MERGE_TRIGGER_COUNT;
  if (!shouldMergeXp && !shouldMergeGold && !shouldMergeHealth) return;

  // Spatial bucket: floor(x/MERGE_RADIUS) × floor(y/MERGE_RADIUS), keyed by type
  const grid: Map<string, LootOrb[]> = new Map();
  for (const orb of orbs) {
    if (!orb.alive || orb.vacuuming) continue;
    if (orb.age < MERGE_MIN_AGE) continue;          // fresh scatter preserved
    if (orb.type === 'xp' && !shouldMergeXp) continue;
    if (orb.type === 'gold' && !shouldMergeGold) continue;
    if (orb.type === 'health' && !shouldMergeHealth) continue;
    const cx = Math.floor(orb.x / MERGE_RADIUS);
    const cy = Math.floor(orb.y / MERGE_RADIUS);
    const key = `${orb.type}:${cx},${cy}`;
    let bucket = grid.get(key);
    if (!bucket) { bucket = []; grid.set(key, bucket); }
    bucket.push(orb);
  }

  // BETA 21.54 — NEIGHBOR-AWARE merge. Dylan: "×2 sitting next to a ×4 that never
  // combine — we end up with hundreds of ×N on the map." Cause: the old per-bucket
  // merge only fused orbs in the SAME cell, so a pile straddling a cell boundary
  // stayed as several separate ×N. Now each still-alive orb is a keeper and absorbs
  // every mergeable same-type orb within MERGE_RADIUS across its OWN cell + the 8
  // neighbor cells — and already-merged orbs keep merging (mergeCount accumulates),
  // so a pile collapses to ONE big ×N. Far fewer orbs on the floor → less FPS drain
  // and they stop carpeting over the enemies.
  for (const keeper of orbs) {
    if (!keeper.alive || keeper.vacuuming || keeper.age < MERGE_MIN_AGE) continue;
    if (keeper.type === 'xp' && !shouldMergeXp) continue;
    if (keeper.type === 'gold' && !shouldMergeGold) continue;
    if (keeper.type === 'health' && !shouldMergeHealth) continue;
    const kcx = Math.floor(keeper.x / MERGE_RADIUS);
    const kcy = Math.floor(keeper.y / MERGE_RADIUS);
    let mergedValue = keeper.value;
    let sumX = keeper.x * keeper.value;
    let sumY = keeper.y * keeper.value;
    let absorbed = 0;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const cell = grid.get(`${keeper.type}:${kcx + dx},${kcy + dy}`);
        if (!cell) continue;
        for (const o of cell) {
          if (o === keeper || !o.alive || o.vacuuming) continue;
          const ex = o.x - keeper.x, ey = o.y - keeper.y;
          if (ex * ex + ey * ey > MERGE_RADIUS2) continue;
          mergedValue += o.value;
          sumX += o.x * o.value;
          sumY += o.y * o.value;
          o.alive = false;
          absorbed += (o.mergeCount ?? 1);   // count the orbs it represented, not just 1
        }
      }
    }
    if (absorbed > 0) {
      keeper.value = mergedValue;
      keeper.x = sumX / mergedValue;
      keeper.y = sumY / mergedValue;
      keeper.mergeCount = (keeper.mergeCount ?? 1) + absorbed;  // BETA 21.34/21.54 — ×N badge
      keeper.pulsePhase = 0;
    }
  }
}

// ─── Spawning ────────────────────────────────────────────────

/**
 * How many orbs to split a drop into based on value.
 * Larger values get split into more orbs so the scatter looks satisfying.
 */
function orbCountForValue(amount: number): number {
  if (amount <= 5)  return 1;
  if (amount <= 20) return 2;
  if (amount <= 60) return 3;
  if (amount <= 150) return 4;
  return 5; // very large drops (elite/boss) burst into 5 orbs
}

/**
 * Convert raw enemy drops into world orbs at the given position.
 * Drops are split into multiple orbs and scattered in a circle pattern.
 * All XP drops become single-type XP orbs (value = amount).
 */
export function spawnDrops(
  state: LootOrbState,
  // BETA 23.6x — CO-OP PHASE 2 part D: drops may carry a per-drop killerPlayerId
  // stamp (part C's AttributedEnemyLootDrop) — it flows onto each spawned orb.
  drops: Array<{ type: string; chance: number; amount: number; killerPlayerId?: string }>,
  worldX: number,
  worldY: number,
  // BETA 23.6x — CO-OP PHASE 2 part D: optional TRAILING default owner (never
  // reorders positionals). Used for drops without their own stamp; per-drop
  // killerPlayerId wins. Omitted/undefined = legacy shared orb.
  ownerPlayerId?: string,
): void {
  // Count total orbs we'll spawn to spread them evenly around a circle
  type OrbPlan = { type: OrbType; value: number; owner?: string };
  const plan: OrbPlan[] = [];

  for (const drop of drops) {
    if (drop.type === 'chest') continue; // handled separately

    let orbType: OrbType | null = null;
    if (drop.type === 'xp')    orbType = 'xp';
    if (drop.type === 'goop')  orbType = 'gold';
    // BETA 21.32 — Dylan: "way too many red plus signs floating around healing me.
    // lets just remove those healing ones completely." Health drops no longer spawn
    // orbs at all — declutters the screen AND removes a free-heal immortality vector.
    // (drop.type === 'health' falls through to the skip below.)
    if (!orbType) continue;

    const count = orbCountForValue(drop.amount);
    const valueEach = drop.amount / count;
    // BETA 23.6x — CO-OP PHASE 2 part D: per-drop stamp beats the call-level default.
    const owner = drop.killerPlayerId ?? ownerPlayerId;
    for (let i = 0; i < count; i++) {
      plan.push({ type: orbType, value: valueEach, owner });
    }
  }

  if (plan.length === 0) return;

  // Distribute angles evenly around a circle, with a random rotation offset
  const baseAngle = Math.random() * Math.PI * 2;
  const angleStep = (Math.PI * 2) / plan.length;

  plan.forEach((entry, i) => {
    const angle = baseAngle + angleStep * i;
    spawnOrbDirected(state, entry.type, entry.value, worldX, worldY, angle, entry.owner);
  });
}

/**
 * Spawn a single orb directly (for manual spawning, testing, etc.)
 */
export function spawnSingleOrb(
  state: LootOrbState,
  type: OrbType,
  value: number,
  worldX: number,
  worldY: number,
): void {
  spawnOrb(state, type, value, worldX, worldY);
}

/** Spawn a single orb with a pre-computed scatter direction (used by spawnDrops). */
function spawnOrbDirected(
  state: LootOrbState,
  type: OrbType,
  value: number,
  worldX: number,
  worldY: number,
  angle: number,
  // BETA 23.6x — CO-OP PHASE 2 part D: trailing optional owner; undefined = shared.
  ownerPlayerId?: string,
): void {
  // BETA 16.37 — O(1) overflow. shift() was O(n) at length 800 (80K
  // element-moves/sec at zombie-flood density). With MAX_ORBS=20K it
  // would be 16M moves/sec → frame killer. Now: when at cap, overwrite
  // the oldest slot in place via a moving cursor.
  const atCap = state.orbs.length >= MAX_ORBS;

  // Speed varies ±20% around base so orbs at same angle still look alive
  const speed = ORB_SCATTER_SPEED * (0.8 + Math.random() * 0.4);

  const orb: LootOrb = {
    id: state.nextId++,
    type,
    value,
    x: worldX,
    y: worldY,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    friction: ORB_FRICTION,
    vacuuming: false,
    vacuumSpeed: 0,
    pulsePhase: Math.random() * Math.PI * 2,
    scale: 0.3,
    alpha: 0.5,
    age: 0,
    alive: true,
    despawnTimer: 0, // unused — orbs never expire
    ownerPlayerId,   // BETA 23.6x — CO-OP PHASE 2 part D: owned-orb identity
  };

  if (atCap) {
    // Overwrite oldest slot in place. _orbWriteCursor wraps modulo
    // MAX_ORBS so we touch every slot in turn before re-using.
    state.orbs[_orbWriteCursor] = orb;
    _orbWriteCursor = (_orbWriteCursor + 1) % MAX_ORBS;
  } else {
    state.orbs.push(orb);
  }
}

/** Spawn a single orb with a random scatter direction (for manual/direct spawning). */
function spawnOrb(
  state: LootOrbState,
  type: OrbType,
  value: number,
  worldX: number,
  worldY: number,
): void {
  spawnOrbDirected(state, type, value, worldX, worldY, Math.random() * Math.PI * 2);
}

// ─── Update ──────────────────────────────────────────────────

// BETA 23.6x — CO-OP PHASE 2 part D: THE owned-orb absorb rule, at one choke
// point. True when the orb is shared (no owner stamp) OR owned by the asking
// player. Today it evaluates true for everything (SP: owner is always p1 or
// undefined, asker is p1) — co-op owner-only absorb flips on with orb colors
// in a later part by simply having owners differ. Never inline this check.
export function orbBelongsTo(orb: LootOrb, playerId: string): boolean {
  return orb.ownerPlayerId === undefined || orb.ownerPlayerId === playerId;
}

export interface LootOrbPlayerInfo {
  x: number;
  y: number;
  magnetBonus: number;  // 0 = no magnet, 1.0 = double radius, etc.
  // BETA 23.6x — CO-OP PHASE 2 part D: which roster player is collecting
  // (machine-local id from the engine). Feeds orbBelongsTo at the pickup +
  // vacuum choke points. SP: 'p1' — every guard evaluates true.
  playerId: string;
}

export interface LootCollectResult {
  xpGained: number;
  goldGained: number;
  healthGained: number;
  orbsCollected: number;
}

export function updateLootOrbs(
  state: LootOrbState,
  dt: number,
  player: LootOrbPlayerInfo,
): LootCollectResult {
  const result: LootCollectResult = {
    xpGained: 0,
    goldGained: 0,
    healthGained: 0,
    orbsCollected: 0,
  };

  const magnetMult = 1 + player.magnetBonus;

  // Chick Magnet — tick down and continuously pull ALL orbs so newly spawned ones get caught too.
  //
  // BETA 16.24 — PSBOT cycle-25 P0.6 (Dylan refinement). Per Dylan: "no
  // chick magnet audio should play if the xp and gold are slowed at
  // first. it wont allways be perfct, but it may fix it most of the
  // time?" Solution: ramp the vacuum speed from 15% → 100% over the
  // first 1.8 seconds (matches chick magnet sample length). Orbs trickle
  // in slowly during the chick magnet stinger, then ramp up to full
  // pull. Cascading level-ups can't all fire in frame 1, so the chick
  // magnet sound has a chance to play out before the level-up sounds.
  // Combined with the P0.7 XP-per-frame cap this should fix it "most of
  // the time" as Dylan said.
  if (state.magnetTimer > 0) {
    state.magnetTimer = Math.max(0, state.magnetTimer - dt);
    const elapsedSinceActivation = MAGNET_DURATION_SEC - state.magnetTimer;
    const rampT = Math.min(1, elapsedSinceActivation / MAGNET_RAMP_SEC);
    const eased = rampT * rampT; // ease-in quad — slow start, fast finish
    const rampedSpeed = MAGNET_VACUUM_SPEED * (0.15 + 0.85 * eased);
    for (const orb of state.orbs) {
      // BETA 23.6x — CO-OP PHASE 2 part D: magnet only keeps pulling orbs the
      // collecting player may absorb (locked design: magnets pull only yours).
      // Evaluates true for everything today — SP owner is always p1/undefined.
      if (orb.alive && orbBelongsTo(orb, player.playerId)) {
        orb.vacuuming = true;
        orb.vacuumSpeed = rampedSpeed;
      }
    }
  }

  for (const orb of state.orbs) {
    if (!orb.alive) continue;

    // Distance² check — avoids sqrt for off-screen orbs entirely
    const dx  = player.x - orb.x;
    const dy  = player.y - orb.y;
    const dist2 = dx * dx + dy * dy;

    // Cull orbs too far to ever matter (feather zone beyond ~viewport diagonal)
    // Exception: vacuuming orbs must survive the trip even if they started far away
    if (dist2 > ORB_CULL_DIST2 && !orb.vacuuming) {
      orb.alive = false;
      continue;
    }

    // Spawn animation always runs (even frozen orbs fade in correctly on approach)
    orb.scale = Math.min(1.0, orb.scale + dt * 5);
    orb.alpha = Math.min(1.0, orb.alpha + dt * 4);

    // Freeze off-screen orbs: skip physics and vacuum.
    // Exception: orbs marked vacuuming (e.g. Chick Magnet) must still move even if distant.
    if (dist2 > ORB_FREEZE_DIST2 && !orb.vacuuming) continue;

    // ── Full update for orbs within range ──
    orb.pulsePhase += dt * 4;
    orb.age += dt;

    const dist = Math.sqrt(dist2); // only computed when needed

    // Determine vacuum radius based on type
    let vacuumRadius: number;
    if (orb.type === 'gold') {
      vacuumRadius = GOLD_VACUUM_RADIUS * magnetMult;
    } else if (orb.type === 'health') {
      vacuumRadius = HEALTH_VACUUM_RADIUS * magnetMult;
    } else {
      vacuumRadius = XP_VACUUM_RADIUS * magnetMult;
    }

    // Delay vacuum for 0.45s after spawn so scatter has visual payoff
    // BETA 23.6x — CO-OP PHASE 2 part D: proximity vacuum gated on ownership
    // (orbBelongsTo = true for everything today; owner-only absorb comes later).
    if (dist < vacuumRadius && (orb.age >= 0.45 || state.magnetTimer > 0)
        && orbBelongsTo(orb, player.playerId)) {
      orb.vacuuming = true;
    }

    // Apply vacuum physics
    if (orb.vacuuming) {
      // Ramp up toward VACUUM_MAX_SPEED for normal vacuum.
      // If vacuumSpeed was already set above max (e.g. by Chick Magnet), never reduce it.
      const speedCap = Math.max(orb.vacuumSpeed, VACUUM_MAX_SPEED);
      orb.vacuumSpeed = Math.min(speedCap, orb.vacuumSpeed + VACUUM_ACCEL * dt);

      if (dist > 1) {
        const nx = dx / dist;
        const ny = dy / dist;
        orb.vx = nx * orb.vacuumSpeed;
        orb.vy = ny * orb.vacuumSpeed;
      }

      // Collection check
      // BETA 23.6x — CO-OP PHASE 2 part D: pickup gated on ownership (always
      // true today; in co-op only the owner absorbs their colored orbs).
      if (dist < COLLECT_RADIUS && orbBelongsTo(orb, player.playerId)) {
        orb.alive = false;
        result.orbsCollected++;

        if (orb.type === 'xp') {
          result.xpGained += orb.value;
        } else if (orb.type === 'gold') {
          result.goldGained += orb.value;
        } else if (orb.type === 'health') {
          result.healthGained += orb.value;
        }

        continue;
      }
    } else {
      // Apply scatter friction
      orb.vx *= Math.max(0, 1 - orb.friction * dt);
      orb.vy *= Math.max(0, 1 - orb.friction * dt);
    }

    // Move
    orb.x += orb.vx * dt;
    orb.y += orb.vy * dt;
  }

  // BETA 17.82 — Periodic merge pass. Fires at most twice per second to
  // keep CPU cost negligible. The merge function itself is gated on per-
  // type alive count so it short-circuits in the common case (<100 orbs
  // of any type alive on the ground).
  _mergeAccum += dt;
  if (_mergeAccum >= MERGE_INTERVAL_SEC) {
    _mergeAccum = 0;
    mergeNearbyOrbs(state.orbs);
  }

  // Clean up dead orbs periodically
  if (state.orbs.length > 50) {
    state.orbs = state.orbs.filter(o => o.alive);
  }

  return result;
}

// ─── Chick Magnet vacuum ─────────────────────────────────────

/**
 * Chick Magnet — force every alive orb into vacuum toward player.
 *
 * BETA 16.24 — Per Dylan (cycle-25 P0.6 refinement): orbs now START SLOW
 * and ramp to MAGNET_VACUUM_SPEED over MAGNET_RAMP_SEC. Initial speed
 * is 15% of full magnet speed so orbs trickle in during the first ~1.8s
 * (matches chick magnet stinger duration). Combined with the P0.7
 * XP-per-frame cap this gives the audio a chance to play out without
 * cascading level-up sounds stomping it. The per-frame ramp logic lives
 * in updateLootOrbs (sets orb.vacuumSpeed each frame from elapsed time).
 */
// BETA 23.6x — CO-OP PHASE 2 part D: trailing optional playerId = who the
// magnet belongs to; owned orbs of OTHER players are skipped (orbBelongsTo).
// undefined = legacy pull-everything (no owner filter). Engine passes the
// machine-local id → SP is byte-identical (owner is always p1/undefined).
export function vacuumAllOrbs(state: LootOrbState, playerId?: string): void {
  const startingSpeed = MAGNET_VACUUM_SPEED * 0.15;
  for (const orb of state.orbs) {
    if (playerId !== undefined && !orbBelongsTo(orb, playerId)) continue;
    if (orb.alive) {
      orb.vacuuming = true;
      orb.vacuumSpeed = startingSpeed;
    }
  }
  state.magnetTimer = MAGNET_DURATION_SEC;
}

/**
 * BETA 20.10 — Vacuum only the orbs within `radius` of (x,y) toward the player.
 * Used by the legendary banana / whoopie monkey-bomb traps: any gold/XP dropped
 * by enemies killed at the trap auto-flies to the player. Per Dylan: "any deaths
 * from those items the gold and xp should auto come to your character."
 */
// BETA 23.6x — CO-OP PHASE 2 part D: trailing optional playerId — same owned-orb
// guard as vacuumAllOrbs (undefined = legacy no-filter; engine passes local id).
export function vacuumOrbsNear(state: LootOrbState, x: number, y: number, radius: number, playerId?: string): void {
  const startingSpeed = MAGNET_VACUUM_SPEED * 0.15;
  const r2 = radius * radius;
  for (const orb of state.orbs) {
    if (!orb.alive || orb.vacuuming) continue;
    if (playerId !== undefined && !orbBelongsTo(orb, playerId)) continue;
    const dx = orb.x - x;
    const dy = orb.y - y;
    if (dx * dx + dy * dy <= r2) {
      orb.vacuuming = true;
      orb.vacuumSpeed = startingSpeed;
    }
  }
}

// ─── Rendering ───────────────────────────────────────────────

// BETA 22.95 — cached 3D XP sphere (built once, drawn scaled per orb).
// Light source matches the game's global sun (top-left) so every shadow and
// highlight in the world agrees.
let _xpSphereCv: HTMLCanvasElement | null = null;
function _getXpSphereSprite(): HTMLCanvasElement {
  if (_xpSphereCv) return _xpSphereCv;
  const S = 64;
  const cv = document.createElement('canvas');
  cv.width = S; cv.height = S;
  const c = cv.getContext('2d')!;
  const r = S / 2 - 1;
  // body: bright core offset to the light, deep blue limb
  const g = c.createRadialGradient(S * 0.38, S * 0.34, r * 0.08, S * 0.5, S * 0.5, r);
  g.addColorStop(0.0, '#dff2ff');
  g.addColorStop(0.25, '#7cc4ff');
  g.addColorStop(0.62, '#2f7fe8');
  g.addColorStop(0.88, '#1a4fb0');
  g.addColorStop(1.0, '#123a85');
  c.fillStyle = g;
  c.beginPath(); c.arc(S / 2, S / 2, r, 0, Math.PI * 2); c.fill();
  // specular hot-spot
  const sg = c.createRadialGradient(S * 0.36, S * 0.30, 0, S * 0.36, S * 0.30, r * 0.34);
  sg.addColorStop(0, 'rgba(255,255,255,0.95)');
  sg.addColorStop(1, 'rgba(255,255,255,0)');
  c.fillStyle = sg;
  c.beginPath(); c.arc(S * 0.36, S * 0.30, r * 0.34, 0, Math.PI * 2); c.fill();
  // faint bounce-light on the dark limb so it reads round, not flat
  const bg = c.createRadialGradient(S * 0.68, S * 0.74, 0, S * 0.68, S * 0.74, r * 0.45);
  bg.addColorStop(0, 'rgba(120,190,255,0.28)');
  bg.addColorStop(1, 'rgba(120,190,255,0)');
  c.fillStyle = bg;
  c.beginPath(); c.arc(S * 0.68, S * 0.74, r * 0.45, 0, Math.PI * 2); c.fill();
  _xpSphereCv = cv;
  return cv;
}

export function renderLootOrbs(
  ctx: CanvasRenderingContext2D,
  state: LootOrbState,
  cameraX: number,
  cameraY: number,
  viewportW: number,
  viewportH: number,
  zoom: number,
): void {
  const halfW = viewportW / 2;
  const halfH = viewportH / 2;
  // BETA 17.33 — cycle-30aq pop-in audit. Was `const pad = 50` (50 px
  // screen-space). At 1920×1080, that's 2.6% of viewport width — orbs
  // appeared mid-screen. Now: max(200, 15% of viewport) so orbs fade in
  // gracefully ~300px outside the visible edge on any size monitor.
  // Compared to world-space objects (shrines/chests use pad=400wu in
  // engine.ts), screen-space is bounded by zoom which doesn't matter
  // for orbs since they're small.
  const pad = Math.max(200, Math.round(Math.max(viewportW, viewportH) * 0.15));

  // Performance: no shadowBlur per orb — too expensive at 100+ orbs.
  // Glow is simulated with a cheap soft-ring pass (drawn once per orb at low alpha).
  // No ctx.save/restore per orb — manage globalAlpha manually.
  ctx.shadowBlur = 0;

  for (const orb of state.orbs) {
    if (!orb.alive) continue;

    const sx = (orb.x - cameraX) * zoom + halfW;
    const sy = (orb.y - cameraY) * zoom + halfH;
    if (sx < -pad || sx > viewportW + pad || sy < -pad || sy > viewportH + pad) continue;

    const baseSize = ORB_SIZES[orb.type] * zoom;
    const pulse    = 1 + Math.sin(orb.pulsePhase) * 0.15;
    // BETA 17.82 — Value-based size scaling for merged orbs. A normal orb
    // has value ~3-6; a merged orb can sit at 50+ after absorbing nearby
    // siblings. Log-scaled so a 10× merge shows ~1.6× size, 100× ≈ 2.2×,
    // 1000× ≈ 2.8×. Capped at 3× so the biggest piles don't dwarf the
    // player. Visual cue: that one's worth more, walk over it first.
    const valueScale = Math.min(3.0, 1 + Math.log10(Math.max(1, orb.value / 4)) * 0.6);
    const size     = baseSize * orb.scale * pulse * valueScale;
    const colors   = ORB_COLORS[orb.type];
    const alpha    = orb.alpha;

    // BETA 17.83 — soft-ring glow pass REMOVED entirely. Dylan: "these
    // should not have the vfx glow arounf them. that causes more lag and
    // such. not needed." Was: an extra 2×-radius arc+fill per orb each
    // frame (at alpha > 0.15). At 685 alive orbs that's 685 extra fill
    // ops × 60 fps = ~41K wasted ops/sec. Pure win — orbs still read
    // clearly via their main fill + (gold) stroke + (xp) inner-highlight.
    // ── Orb shape ────────────────────────────────────────────────────
    ctx.globalAlpha = alpha;

    if (orb.type === 'health') {
      ctx.fillStyle = colors.fill;
      const arm   = size * 0.5;
      const thick = size * 0.3;
      ctx.fillRect(sx - arm, sy - thick / 2, arm * 2, thick);
      ctx.fillRect(sx - thick / 2, sy - arm, thick, arm * 2);
      ctx.fillStyle = colors.inner;
      ctx.fillRect(sx - arm * 0.5, sy - thick * 0.25, arm, thick * 0.5);
      ctx.fillRect(sx - thick * 0.25, sy - arm * 0.5, thick * 0.5, arm);
    } else if (orb.type === 'gold') {
      // BETA 22.89 — TOKEN, not gold coin (Dylan: "a token looks like a gold
      // coin but with the flatness and ridges around the edges"). Pure vector
      // draw — cheaper than the old "$" fillText, no sprite spam.
      ctx.fillStyle = colors.fill;
      ctx.beginPath();
      ctx.arc(sx, sy, size, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#aa7700';
      ctx.lineWidth = Math.max(1, size * 0.16);
      ctx.beginPath();
      ctx.arc(sx, sy, size * 0.94, 0, Math.PI * 2);   // flat rim
      ctx.stroke();
      // ridge ticks + flat inner face — only when big enough to read
      // (keeps the 14.64 perf discipline: no fine detail on tiny orbs)
      if (size >= 6) {
        ctx.beginPath();
        for (let k = 0; k < 8; k++) {
          const a = (k / 8) * Math.PI * 2 + 0.39;
          ctx.moveTo(sx + Math.cos(a) * size * 0.76, sy + Math.sin(a) * size * 0.76);
          ctx.lineTo(sx + Math.cos(a) * size * 0.94, sy + Math.sin(a) * size * 0.94);
        }
        ctx.lineWidth = Math.max(1, size * 0.10);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(sx, sy, size * 0.52, 0, Math.PI * 2);
        ctx.stroke();
      }
    } else {
      // BETA 22.95 — XP = 3D BLUE SPHERE (Dylan: "the xp orbs need to be 3d
      // blue spheres"). Pre-rendered once to an offscreen canvas (radial
      // gradient lit from the game's global top-left light + specular dot),
      // then 1 drawImage per orb — same perf class as the old two-fill
      // diamond, honors the 17.83 no-per-orb-gradient discipline.
      const sph = _getXpSphereSprite();
      ctx.drawImage(sph, sx - size, sy - size, size * 2, size * 2);
    }

    // BETA 21.34 — BUNDLE BADGE (Dylan: "orbs near each other bundle, then a number
    // on them — 2x/3x/4x — or color change, to show they're worth way more"). A merged
    // orb gets a tier-colored ring + a ×N badge (N = how many it bundled) so the juicy
    // piles read from across the screen. Gated on size so the swarm of tiny single
    // orbs stays clean + cheap (no per-orb text on value-1 orbs).
    const _mc = orb.mergeCount ?? 1;
    if (_mc >= 2 && size >= 7) {
      const _tier = _mc >= 16 ? '#66e0ff' : _mc >= 8 ? '#ff66ff' : _mc >= 4 ? '#ffa030' : '#ffffff';
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = _tier;
      ctx.lineWidth = Math.max(1.5, size * 0.16);
      ctx.beginPath();
      ctx.arc(sx, sy, size * 1.25, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = _tier;
      ctx.font = `bold ${Math.max(9, size * 0.95)}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      // BETA 21.36 — cap the badge at ×99 (Dylan: "up to 99x").
      ctx.fillText(`×${Math.min(99, _mc)}`, sx, sy - size * 1.7);
    }
  }

  // Restore canvas state
  ctx.globalAlpha = 1;
}
